'use client'

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/src/components/ui/button'
import { cn } from '@/src/utils/styles'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <section
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative group', className)}
          {...props}
        >
          {children}
        </section>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className='overflow-hidden relative'>
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'ml-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

interface CarouselGradientContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  side: 'left' | 'right'
  children: React.ReactNode
  hideGradient?: boolean
}

const CarouselGradientContainer = React.forwardRef<
  HTMLDivElement,
  CarouselGradientContainerProps
>(({ side, children, className, hideGradient = false, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-0 bottom-0 w-32',
        side === 'left' ? 'left-0 ml-[-1px]' : 'right-0',
        !hideGradient &&
          (side === 'left'
            ? 'bg-gradient-to-l from-transparent to-background'
            : 'bg-gradient-to-r from-transparent to-background'),
        'opacity-0 group-hover:opacity-100 2xl:opacity-100 transition-opacity duration-700 ease-default',
        'flex flex-col justify-center items-center',
        'cursor-pointer',
        'group/button-container',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})
CarouselGradientContainer.displayName = 'CarouselGradientContainer'

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    hideGradient?: boolean
  }
>(
  (
    {
      className,
      variant = 'secondary',
      size = 'md',
      hideGradient = false,
      ...props
    },
    ref,
  ) => {
    const { scrollPrev, canScrollPrev } = useCarousel()

    return (
      <>
        {canScrollPrev && (
          <CarouselGradientContainer
            side='left'
            onClick={scrollPrev}
            hideGradient={hideGradient}
          >
            <Button
              ref={ref}
              variant={variant}
              size={size}
              className={cn(
                'p-3',
                'rounded-full opacity-0 group-hover:opacity-100 transition-opacity',
                'bg-surface2-100 group-hover/button-container:bg-surface3-100 focus:ring-0',
                className,
              )}
              disabled={!canScrollPrev}
              {...props}
            >
              <ArrowLeft className='h-6 w-6' />
              <span className='sr-only'>Previous slide</span>
            </Button>
          </CarouselGradientContainer>
        )}
      </>
    )
  },
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    hideGradient?: boolean
  }
>(
  (
    {
      className,
      variant = 'secondary',
      size = 'md',
      hideGradient = false,
      ...props
    },
    ref,
  ) => {
    const { scrollNext, canScrollNext } = useCarousel()

    return (
      <>
        {canScrollNext && (
          <CarouselGradientContainer
            side='right'
            onClick={scrollNext}
            hideGradient={hideGradient}
          >
            <Button
              ref={ref}
              variant={variant}
              size={size}
              className={cn(
                'p-3',
                'rounded-full opacity-0 group-hover:opacity-100 transition-opacity',
                'bg-surface2-100 group-hover/button-container:bg-surface3-100 focus:ring-0',
                className,
              )}
              disabled={!canScrollNext}
              {...props}
            >
              <ArrowRight className='h-6 w-6' />
              <span className='sr-only'>Next slide</span>
            </Button>
          </CarouselGradientContainer>
        )}
      </>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    api?: CarouselApi
  }
>(({ className, api: externalApi, ...props }, ref) => {
  // Use external API if provided, otherwise get from context
  const contextApi = React.useContext(CarouselContext)?.api
  const api = externalApi || contextApi

  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)

  React.useEffect(() => {
    if (!api) {
      return
    }

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap())
    }

    const onScroll = () => {
      const progress = api.scrollProgress()
      const snapCount = api.scrollSnapList().length

      // Clamp progress to 0-1 range first to handle overshoot
      const clampedProgress = Math.max(0, Math.min(progress, 1))

      // Calculate which slide is currently closest based on clamped scroll progress
      const rawIndex = Math.round(clampedProgress * (snapCount - 1))
      // Ensure index is within valid range (extra safety)
      const currentIndex = Math.max(0, Math.min(rawIndex, snapCount - 1))
      setCurrent(currentIndex)
    }

    api.on('select', onSelect)
    api.on('scroll', onScroll)
    api.on('reInit', onSelect)

    return () => {
      api.off('select', onSelect)
      api.off('scroll', onScroll)
      api.off('reInit', onSelect)
    }
  }, [api])

  if (count <= 1 || !api) return null

  return (
    <div
      ref={ref}
      className={cn('flex items-center justify-center gap-3', className)}
      {...props}
    >
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          onClick={() => api?.scrollTo(index)}
          className={cn(
            'h-2 w-2 rounded-full transition-colors',
            current === index ? 'bg-primary' : 'bg-border1 hover:bg-secondary',
          )}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  )
})
CarouselDots.displayName = 'CarouselDots'

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
}
